import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import {
  Ticket,
  Zap,
  AlertCircle,
  ThumbsUp,
  Clock,
  CheckCircle2,
  Star,
  MessageSquare
} from 'lucide-react';

// Category display names (backend sends these now, but keep as fallback)
const CATEGORY_NAMES = {
  'IT': 'IT Support',
  'Ignite': 'Ignite',
  'CE': 'Clinical Excellence',
  'HR': 'Human Resources',
  'Concur': 'Concur'
};

// Alias for backwards compatibility in SLA table
const PROJECT_NAMES = CATEGORY_NAMES;

// Star rating component
const StarRating = ({ rating }) => {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={cn(
            "w-4 h-4",
            star <= rating ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/30"
          )}
        />
      ))}
    </div>
  );
};

export const TicketSnapshotSection = ({ operations, timeRange, satisfaction: satisfactionData, className }) => {
  // Check if we have real Jira data
  const hasJiraData = operations?.source === 'jira_live' || operations?.tickets?.source === 'jira_live';
  const hasError = operations?.source === 'error' || operations?.tickets?.source === 'error';
  const errorMessage = operations?.tickets?.error || operations?.error || 'Connection failed';
  
  // Get ticket data
  const ticketData = operations?.tickets || operations;
  const categories = ticketData?.categories || ticketData?.projects || [];
  const totals = ticketData?.totals || { open: 0, closed: 0 };
  const slaMetrics = ticketData?.sla_metrics || {};
  const timeRangeDays = ticketData?.time_range_days || 7;
  
  // Use new satisfaction data if available, fallback to operations satisfaction
  const hasSatisfactionData = satisfactionData?.success && satisfactionData?.data;
  const satisfactionPct = hasSatisfactionData 
    ? satisfactionData.data.satisfactionPercentage 
    : (ticketData?.satisfaction?.satisfaction_percent ?? null);
  const latestReviews = hasSatisfactionData 
    ? (satisfactionData.data.latestReviews || [])
    : [];
  
  // If error, show error message
  if (!operations || hasError || !hasJiraData) {
    return (
      <Card className={cn('p-6 bg-card/80 backdrop-blur-sm border-border/50', className)} data-testid="ticket-snapshot-section">
        <div className="text-center py-8 text-muted-foreground">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-[rgb(var(--destructive))] opacity-50" />
          <p className="font-semibold text-[rgb(var(--destructive))]">Err: Unable to connect to Jira</p>
          <p className="text-sm mt-2">{errorMessage}</p>
          <p className="text-xs mt-2 opacity-70">Ticket data will appear here when Jira is available</p>
        </div>
      </Card>
    );
  }
  
  return (
    <div className={cn('space-y-6', className)} data-testid="ticket-snapshot-section">
      {/* Header */}
      <Card className="p-6 bg-card/80 backdrop-blur-sm border-border/50">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Ticket Snapshot
          </h3>
          <Badge className="bg-[rgb(var(--success))]/10 text-[rgb(var(--success))] border-[rgb(var(--success))]/20 gap-1">
            <Zap className="w-3 h-3" />
            Live from Jira ({timeRangeDays}d)
          </Badge>
        </div>

        {/* Totals Row */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
          <div className="p-4 rounded-xl bg-muted/30 border border-border/50 text-center">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">TMC Support Open</p>
            <p className="text-3xl font-bold text-primary" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {totals.open}
            </p>
          </div>
          <div className="p-4 rounded-xl bg-muted/30 border border-border/50 text-center">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Closed ({timeRangeDays}d)</p>
            <p className="text-3xl font-bold text-[rgb(var(--success))]" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {totals.closed}
            </p>
          </div>
          <div className="p-4 rounded-xl bg-muted/30 border border-border/50 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <ThumbsUp className="w-4 h-4 text-primary" />
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Satisfaction ({timeRangeDays}d)</p>
            </div>
            <p className="text-3xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {satisfactionPct !== null && satisfactionPct !== undefined ? `${satisfactionPct}%` : 'N/A'}
            </p>
            <p className="text-xs text-muted-foreground">TMCSUPP only</p>
          </div>
        </div>

        {/* Latest Satisfaction Reviews */}
        {latestReviews.length > 0 && (
          <div className="mb-6">
            <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-primary" />
              Latest Satisfaction Reviews
            </h4>
            <div className="space-y-3">
              {latestReviews.map((review, idx) => (
                <div 
                  key={review.ticketKey || idx}
                  className="p-3 rounded-lg bg-muted/20 border border-border/30"
                  data-testid={`review-${idx}`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <StarRating rating={review.rating} />
                    <span className="text-xs text-muted-foreground">{review.ticketKey}</span>
                  </div>
                  <p className="text-sm text-foreground/90 italic">"{review.comment}"</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Open Tickets by Category */}
        <div className="mb-6">
          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Ticket className="w-4 h-4 text-primary" />
            Open Tickets by Category
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {categories.map((cat) => (
              <div 
                key={cat.category} 
                className="p-3 rounded-lg bg-muted/20 border border-border/30 text-center"
                data-testid={`open-${cat.category?.toLowerCase()}`}
              >
                <p className="text-xs text-muted-foreground mb-1">{cat.display_name || CATEGORY_NAMES[cat.category] || cat.category}</p>
                <p className="text-xl font-bold text-primary">{cat.open}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Closed Tickets by Category */}
        <div>
          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-[rgb(var(--success))]" />
            Closed Tickets ({timeRangeDays}d) by Category
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {categories.map((cat) => (
              <div 
                key={cat.category} 
                className="p-3 rounded-lg bg-muted/20 border border-border/30 text-center"
                data-testid={`closed-${cat.category?.toLowerCase()}`}
              >
                <p className="text-xs text-muted-foreground mb-1">{cat.display_name || CATEGORY_NAMES[cat.category] || cat.category}</p>
                <p className="text-xl font-bold text-[rgb(var(--success))]">{cat.closed}</p>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* SLA Metrics Card */}
      <Card className="p-6 bg-card/80 backdrop-blur-sm border-border/50">
        <h4 className="text-sm font-semibold mb-4 flex items-center gap-2">
          <Clock className="w-4 h-4 text-primary" />
          SLA Performance ({timeRangeDays}d)
        </h4>
        
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/50">
                <th className="text-left py-2 px-3 text-muted-foreground font-medium">Project</th>
                <th className="text-center py-2 px-3 text-muted-foreground font-medium">Resolved</th>
                <th className="text-center py-2 px-3 text-muted-foreground font-medium">First Response %</th>
                <th className="text-center py-2 px-3 text-muted-foreground font-medium">Resolution %</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(slaMetrics).length > 0 ? (
                Object.entries(slaMetrics).map(([project, metrics]) => (
                  <tr key={project} className="border-b border-border/30">
                    <td className="py-2 px-3 font-medium">{PROJECT_NAMES[project] || project}</td>
                    <td className="py-2 px-3 text-center">{metrics.resolved || 0}</td>
                    <td className="py-2 px-3 text-center">
                      {metrics.tfr_percent !== null && metrics.tfr_percent !== undefined ? (
                        <span className={cn(
                          "font-mono",
                          metrics.tfr_percent >= 95 && "text-[rgb(var(--success))]",
                          metrics.tfr_percent >= 80 && metrics.tfr_percent < 95 && "text-[rgb(var(--warning))]",
                          metrics.tfr_percent < 80 && "text-[rgb(var(--destructive))]"
                        )}>
                          {metrics.tfr_percent}%
                        </span>
                      ) : (
                        <span className="text-muted-foreground">N/A</span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-center">
                      {metrics.ttr_percent !== null && metrics.ttr_percent !== undefined ? (
                        <span className={cn(
                          "font-mono",
                          metrics.ttr_percent >= 95 && "text-[rgb(var(--success))]",
                          metrics.ttr_percent >= 80 && metrics.ttr_percent < 95 && "text-[rgb(var(--warning))]",
                          metrics.ttr_percent < 80 && "text-[rgb(var(--destructive))]"
                        )}>
                          {metrics.ttr_percent}%
                        </span>
                      ) : (
                        <span className="text-muted-foreground">N/A</span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="py-4 text-center text-muted-foreground">
                    <p>SLA data not available</p>
                    <p className="text-xs mt-1">No resolved tickets with SLA data in selected time range</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

// Keep OperationsSection as an alias for backwards compatibility
export const OperationsSection = TicketSnapshotSection;
